import React from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  useTheme,
} from "@mui/material";

function InputValue({ name, multiple, value }) {
  if (name == null) {
    return null;
  }

  if (multiple) {
    return value.map((o) => (
      <input key={o.id} type="hidden" name={name + "[]"} value={o.id ?? ""} />
    ));
  }

  return <input type="hidden" name={name} value={value?.id ?? ""} />;
}

/**
 * @typedef AutocompleteSelectOption
 * @property {string} id
 * @property {string} name
 *
 * @typedef AutocompleteSelectProps
 * @property {AutocompleteSelectOption[]} options
 * @property {boolean} [loading]
 * @property {string} label
 * @property {(option: AutocompleteSelectOption) => void} [onChange]
 * @property {string} [name]
 * @property {AutocompleteSelectOption} [value]
 * @property {AutocompleteSelectOption} [defaultValue]
 * @property {boolean} [disabled]
 * @property {boolean} [readOnly]
 * @property {boolean} [required]
 * @property {boolean} [multiple]
 * @property {string} [size]
 * @property {boolean} [autoFocus]
 * @property {Function} [groupBy]
 * @property {boolean} [disableClearable]
 * @property {string} [warningText] // optional warning text shown next to label in orange
 *
 * @param {AutocompleteSelectProps} props
 */
export default function AutocompleteSelect(props) {
  const theme = useTheme();

  const {
    options,
    loading,
    label,
    onChange,
    name,
    disabled,
    value: externalValue,
    readOnly,
    required: externalRequired,
    defaultValue,
    multiple,
    size,
    autoFocus,
    groupBy,
    disableClearable,
    warningText,
    onFocus,
    onBlur,
  } = props;
  const [internalValue, setInternalValue] = React.useState(
    defaultValue ?? (multiple ? [] : null),
  );
  const value = externalValue === undefined ? internalValue : externalValue;
  const required =
    multiple && externalRequired ? value.length === 0 : externalRequired;

  return (
    <>
      <InputValue name={name} value={value} multiple={multiple} />
      <Autocomplete
        size={size}
        multiple={multiple}
        autoHighlight={true}
        disableCloseOnSelect={multiple}
        disabled={disabled}
        value={externalValue ?? value}
        loading={loading}
        options={options}
        getOptionLabel={(opt) => opt.name}
        getOptionKey={(opt) => opt.id}
        readOnly={readOnly}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderInput={(params) => (
          <TextField
            label={
              warningText ? (
                <span>
                  {label}
                  {" - "}
                  <span style={{ color: theme.palette.warning.dark }}>
                    {warningText}
                  </span>
                </span>
              ) : (
                label
              )
            }
            autoFocus={autoFocus}
            required={required}
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        onChange={(_, value) => {
          setInternalValue(value);
          onChange?.(value);
        }}
        groupBy={groupBy}
        disableClearable={disableClearable}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </>
  );
}
